/**
 * Where the ad-preview iframe is loaded from.
 *
 * The frame is sandboxed without `allow-same-origin`, so on the app's own
 * origin it runs at an opaque origin whose per-instance nonce goes into
 * Chrome's cache partition key, and nothing it fetches is ever reused. A stable
 * origin fixes that, and it has to be a separate registrable domain rather than
 * a `*.hightouch.com` subdomain: a subdomain can set `Domain=hightouch.com`
 * cookies and toss a session onto the app, and what runs in this frame is
 * agent-authored JavaScript.
 */

const FRAME_ORIGIN = "https://frame.hightouchusercontent.com";

const FRAME_PREFIX =
  import.meta.env.VITE_VERCEL_ENV === "production" ? "production" : "preview";

export interface PreviewFrameSource {
  src: string;
  /**
   * `postMessage` target, and the origin inbound messages are checked against.
   *
   * `null` marks the same-origin fallback, which must never be given
   * `allow-same-origin`: on the app's own origin the framed agent code could
   * reach the parent document and strip its own sandbox.
   */
  origin: string | null;
}

const SAME_ORIGIN: PreviewFrameSource = {
  src: "/ad-preview-frame.html",
  origin: null,
};

/**
 * Falls back to the same-origin frame when the flag is off, or when there is no
 * commit SHA to address a published frame with (local `vite dev`).
 */
export function resolvePreviewFrameSource(
  separateOriginEnabled: boolean,
): PreviewFrameSource {
  if (!separateOriginEnabled || typeof document === "undefined") {
    return SAME_ORIGIN;
  }

  // Published on the mount node by `injectCommitSHAPlugin` (see
  // `vite.config.ts`), which leaves the placeholder in place when it cannot
  // resolve one.
  const sha = document.getElementById("react")?.dataset.commitSha;
  if (!sha || sha === "unknown" || sha.includes("{{")) return SAME_ORIGIN;

  return {
    src: `${FRAME_ORIGIN}/${FRAME_PREFIX}/${sha}/ad-preview-frame.html`,
    origin: FRAME_ORIGIN,
  };
}
